<template>
  <div id="modal-target"></div>
  <div class="bg-waves">
    <header>
      <navbar></navbar>
    </header>
    <main class="min-vh-100 pt-navbar">
      <StemPlayerRenderer
        :player="player"
        ref="stemPlayerPanelWrapper"
      ></StemPlayerRenderer>
      <!-- https://stackoverflow.com/questions/66210222/vue-refresh-page-on-param-id-changes-for-the-same-page -->
      <router-view :key="$route.fullPath"></router-view>
    </main>
    <app-footer></app-footer>
    <notificationGroup group="default">
      <div class="position-fixed z-index-top bottom-0 start-0 w-100">
        <notification v-slot="{ notifications, close }">
          <div
            v-for="notification in notifications"
            :key="notification.id"
            class="alert bg-secondary text-white mb-0 text-center rounded-0"
            role="alert"
          >
            <strong>{{ notification.title }}</strong> &mdash;
            {{ notification.text }}
            <button
              class="btn btn-secondary py-1 px-2 ms-2"
              @click="close(notification.id)"
            >
              <i class="bi bi-x-lg text-white"></i>
            </button>
          </div>
        </notification>
      </div>
    </notificationGroup>
  </div>
</template>

<script>
// import HelloWorld from "./components/HelloWorld.vue";
import '@aws-amplify/ui-vue/styles.css';
import { inject } from 'vue';
import { Hub, Cache } from 'aws-amplify';
import Navbar from './components/Navbar.vue';
import AppFooter from './components/AppFooter.vue';
import { record, setDefault } from './services/analytics';
import { PAGE_LOAD, NAVIGATE } from './consts/analytics';
import { v4 as createUuid } from 'uuid';
import currentUserCredentials from './services/currentUserCredentials';
import StemPlayerRenderer from './components/StemPlayerRenderer.vue';
import adminTracksDb from './services/adminTracksDb';

// we create a new correlation id. We dont store it.
// https://www.termsfeed.com/blog/analytics-tools-gdpr-consent/
setDefault('cid', createUuid());

export default {
  name: 'App',
  components: {
    Navbar,
    AppFooter,
    StemPlayerRenderer,
  },
  setup() {
    const player = inject('player');

    return {
      player,
    };
  },
  async beforeMount() {
    Hub.listen('auth', (data) => {
      const event = data.payload.event;
      if (
        event === 'signOut' ||
        event === 'oAuthSignOut' ||
        event === 'signIn'
      ) {
        Cache.clear();
        adminTracksDb.truncate();
        this.$acl.refresh();
      }
    });

    // if this window is a child window (clicking on download mix for example can create new tabs/windows)
    // then message the parent when we sign in or out.
    if (window.opener) {
      Hub.listen('auth', (data) => {
        if (data.payload.event === 'signIn') {
          window.opener.postMessage('child_signIn');
        }
        if (data.payload.event === 'signOut') {
          window.opener.postMessage('child_signOut');
        }
      });
    }

    try {
      const creds = await currentUserCredentials();
      setDefault('id', creds.identityId);
    } catch (err) {
      // do nothing
    }

    record(PAGE_LOAD);
  },
  watch: {
    $route(v) {
      record(NAVIGATE, { fullPath: v.fullPath });

      // const { meta } = to;
      // update og tag url
      document
        .querySelector('meta[property="og:url"]')
        .setAttribute('content', window.location.href);
    },
  },
};
</script>

<style lang="scss">
html {
  scroll-behavior: inherit !important;
}

body {
  scroll-behavior: smooth !important;
}

.font-2 {
  font-family: 'BenchNine', sans-serif;
}

h1,
h2,
h3,
h4,
h5,
h6 {
  font-family: 'BenchNine', sans-serif;
}

.card-header h5,
.header {
  font-family: 'Ubuntu', sans-serif;
  color: $primary;
  font-size: 0.9em;
}

#app {
  font-family: 'Ubuntu', sans-serif;
  font-weight: 300;
  background-color: $bg-color;
  color: #fff;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}
.min-vh-75 {
  min-height: 75vh;
}
.min-vh-50 {
  min-height: 50vh;
}
.min-vh-25 {
  min-height: 25vh;
}
.bg-body {
  background-color: $bg-color !important;
}
.bg-body-op {
  background-color: rgba(33, 41, 49, 0.9);
}
.bg-mask {
  background-color: rgba(0, 0, 0, 0.9);
}
.bg-waves {
  // background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='2500' height='1500' preserveAspectRatio='none'%3E%3Cg mask='url(%26quot;%23SvgjsMask1153%26quot;)' fill='none' stroke='rgba(0, 206, 224, 0.23)' stroke-width='2'%3E%3Cpath d='M-764.36 336c125 144.2 375 647.8 625 721 250 73.2 375-396 625-355s375 643.2 625 560c250-83.2 375-972 625-976s472.13 931.6 625 956c152.87 24.4 111.49-667.2 139.36-834'/%3E%3Cpath d='M-210.243 448c125.003 143.2 375.003 745 625 716 250.003-29 375.003-840.4 625-861 250.003-20.6 375.003 712.2 625 758 250.003 45.8 375.003-560 625-529 250.003 31 582.953 709.2 625 684 42.053-25.2-331.807-648-414.757-810'/%3E%3Cpath d='M-526.53 865c125-52.6 375-257.8 625-263 250-5.2 375 264.2 625 237 250-27.2 375-456 625-373s375 821.2 625 788c250-33.2 519.69-949.6 625-954 105.31-4.4-78.78 745.6-98.47 932'/%3E%3Cpath d='M-200.237 827c124.997-105.2 374.997-611.2 625-526 249.997 85.2 374.997 976.2 625 952 249.997-24.2 374.997-1007.8 625-1073 249.997-65.2 374.997 651.4 625 747 249.997 95.6 584.957-303.2 625-269 40.047 34.2-339.813 352-424.763 440'/%3E%3C/g%3E%3Cdefs%3E%3Cmask id='SvgjsMask1153'%3E%3Cpath fill='%23fff' d='M0 0h2500v1500H0z'/%3E%3C/mask%3E%3C/defs%3E%3C/svg%3E");
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' version='1.1' xmlns:xlink='http://www.w3.org/1999/xlink' xmlns:svgjs='http://svgjs.com/svgjs' width='2500' height='1500' preserveAspectRatio='none' viewBox='0 0 2500 1500'%3e%3cg mask='url(%26quot%3b%23SvgjsMask1027%26quot%3b)' fill='none'%3e%3cpath d='M -24.922490821009774%2c1183 C 58.48%2c1034.8 225.28%2c484.2 392.0775091789902%2c442 C 558.88%2c399.8 642.28%2c998.8 809.0775091789902%2c972 C 975.88%2c945.2 1059.28%2c302.8 1226.07750917899%2c308 C 1392.88%2c313.2 1476.28%2c1002.8 1643.07750917899%2c998 C 1809.88%2c993.2 1893.28%2c323.4 2060.07750917899%2c284 C 2226.88%2c244.6 2310.28%2c789.8 2477.07750917899%2c801 C 2643.88%2c812.2 2889.49%2c284.2 2894.07750917899%2c340 C 2898.66%2c395.8 2578.82%2c932 2500%2c1080' stroke='rgba(219%2c 219%2c 219%2c 0.23)' stroke-width='2'%3e%3c/path%3e%3cpath d='M -63.73179501288905%2c905 C 19.67%2c799.8 186.47%2c307.6 353.26820498711095%2c379 C 520.07%2c450.4 603.47%2c1261.8 770.268204987111%2c1262 C 937.07%2c1262.2 1020.47%2c445.4 1187.268204987111%2c380 C 1354.07%2c314.6 1437.47%2c939.4 1604.268204987111%2c935 C 1771.07%2c930.6 1854.47%2c294.8 2021.268204987111%2c358 C 2188.07%2c421.2 2271.47%2c1172.8 2438.268204987111%2c1251 C 2605.07%2c1329.2 2842.92%2c822.8 2855.268204987111%2c749 C 2867.61%2c675.2 2571.05%2c855.4 2500%2c882' stroke='rgba(219%2c 219%2c 219%2c 0.23)' stroke-width='2'%3e%3c/path%3e%3cpath d='M -714.6378470712771%2c852 C -631.24%2c773 -464.44%2c471 -297.63784707127706%2c457 C -130.84%2c443 -47.44%2c798.2 119.36215292872294%2c782 C 286.16%2c765.8 369.56%2c282.2 536.3621529287229%2c376 C 703.16%2c469.8 786.56%2c1227.8 953.3621529287229%2c1251 C 1120.16%2c1274.2 1203.56%2c556.2 1370.362152928723%2c492 C 1537.16%2c427.8 1620.56%2c946.4 1787.362152928723%2c930 C 1954.16%2c913.6 2061.83%2c415.8 2204.362152928723%2c410 C 2346.89%2c404.2 2440.87%2c802.8 2500%2c901' stroke='rgba(219%2c 219%2c 219%2c 0.23)' stroke-width='2'%3e%3c/path%3e%3cpath d='M -551.5697593483683%2c431 C -468.17%2c512.8 -301.37%2c837.8 -134.5697593483683%2c840 C 32.23%2c842.2 115.63%2c415.4 282.4302406516317%2c442 C 449.23%2c468.6 532.63%2c946.8 699.4302406516317%2c973 C 866.23%2c999.2 949.63%2c595.2 1116.4302406516317%2c573 C 1283.23%2c550.8 1366.63%2c850.4 1533.4302406516317%2c862 C 1700.23%2c873.6 1783.63%2c615 1950.4302406516317%2c631 C 2117.23%2c647 2257.52%2c1028 2367.4302406516317%2c942 C 2477.34%2c856 2473.49%2c349.2 2500%2c201' stroke='rgba(219%2c 219%2c 219%2c 0.23)' stroke-width='2'%3e%3c/path%3e%3c/g%3e%3cdefs%3e%3cmask id='SvgjsMask1027'%3e%3crect width='2500' height='1500' fill='white'%3e%3c/rect%3e%3c/mask%3e%3c/defs%3e%3c/svg%3e");
  background-size: cover;
  background-attachment: fixed;
}
.bg-darker {
  background-color: $darker !important;
}
.bg-dark {
  background-color: #343a40 !important;
}
.bg-darker {
  background-color: #232323 !important;
}
.bg-dark-9 {
  background-color: rgba(0, 0, 0, 0.9) !important;
}
.bg-dark-8 {
  background-color: rgba(0, 0, 0, 0.8) !important;
}
.bg-dark-7 {
  background-color: rgba(0, 0, 0, 0.7) !important;
}
.bg-dark-6 {
  background-color: rgba(0, 0, 0, 0.6) !important;
}
.bg-dark-4 {
  background-color: rgba(0, 0, 0, 0.4) !important;
}
.bg-dark-2 {
  background-color: rgba(0, 0, 0, 0.2) !important;
}
.bg-brand {
  background-color: $brand-color !important;
}

.bg-light-3 {
  background-color: rgba(255, 255, 255, 0.3) !important;
}

.bg-light-4 {
  background-color: rgba(255, 255, 255, 0.4) !important;
}

.bg-light-7 {
  background-color: rgba(255, 255, 255, 0.7) !important;
}

// btn.bg-brand:active,
// btn.bg-brand:hover {
//   background-color: white !important;
// }

.bg-brand-2 {
  background-color: $brand-color-2;
}
.color-brand {
  color: $brand-color !important;
}
.color-brand-2 {
  color: $brand-color-2;
}
.color-brand-3 {
  color: $brand-color-3;
}
// footer {
//   border-top: 1px solid #222;

//   background-color: $bg-color;
// }

// a {
//   color: $link-color;
// }
// a:hover {
//   color: $brand-color;
// }

.color-brand {
  color: $brand-color;
}

.border-brand {
  border-color: $brand-color !important;
}

.border-dashed {
  border-style: dashed !important;
}

.border-thick {
  border-width: 3px !important;
}

.min-vh-almost-100 {
  min-height: calc(100vh - 60px);
}

.top-navbar {
  top: $navbarOffset;
}

.margin-top-min-navbar {
  margin-top: -75px;
}

// to cancel of pt-4 / py-4
.mt-min-4 {
  margin-top: -1.5rem !important;
}

@media (max-width: 991.98px) {
  .min-vh-100-sm {
    min-height: 100vh;
  }

  .me-4-sm {
    margin-right: 1.5rem !important;
  }
}

@media (min-width: 991px) {
  .min-vh-100-lg {
    min-height: 100vh;
  }

  .min-vh-75-lg {
    min-height: 75vh;
  }

  .h-75vh-lg {
    height: 75vh;
  }

  .position-absolute-lg {
    position: absolute;
  }

  .w-75-lg {
    width: 75%;
  }

  .position-relative-lg {
    position: relative;
  }

  .me-4-lg {
    margin-right: 1.5rem !important;
  }

  .mt--navbar-lg {
    margin-top: -$navbarOffset;
  }
}

.display-8 {
  font-size: 1.25rem;
  font-weight: 300;
}

.display-7 {
  font-size: 2rem;
  font-weight: 300;
}

.pt-navbar {
  padding-top: $navbarOffset;
}

.mt--navbar {
  margin-top: -$navbarOffset;
}

.z-index-999 {
  z-index: 999;
}

.z-index-1000 {
  z-index: 1000;
}

.z-index-top {
  z-index: 99999;
}

.d-top {
  position: relative;
  z-index: 9999;
}

.body-mask {
  position: absolute;
  width: 100%;
  height: 100%;
}

.form-check-input:checked {
  background-color: $brand-color !important;
  border-color: $brand-color;
}

.btn-xl {
  padding: 0.5rem 1rem;
  font-size: 2rem;
  border-radius: 0.3rem;
}

.container-narrow {
  max-width: 1000px;
}

.mw-500 {
  max-width: 500px;
}
.border-top-dark-gray {
  border-top: 1px solid #222;
}
.border-bottom-dark-gray {
  border-bottom: 1px solid #222;
}
label.required:after {
  content: '*';
  color: #d73a49;
  padding-left: 5px;
}

.text-justify {
  text-align: justify !important;
}

body.modal-open {
  height: 100vh;
  overflow: hidden;
}

input[disabled] {
  color: #aaa !important;
}

.pointer-events-none {
  pointer-events: none;
}

.bg-dark-gray {
  background-color: rgb(35, 35, 35) !important;
}

.height-track-row {
  height: 60px !important;
}

.list-group-item-dark {
  background-color: $darker;
  border: 1px solid rgba(0, 0, 0, 0.325);
  color: rgb(220, 220, 220);
}

.list-group-item-dark:hover {
  background-color: rgb(37, 37, 37) !important;
  color: white !important;
}

.list-group-item-dark.active:hover {
  background-color: #01a4b3 !important;
  color: black !important;
}

button.list-group-item-dark:active,
button.list-group-item-dark.active,
a.list-group-item-dark:active,
a.list-group-item-dark.active {
  background-color: $primary !important;
  color: black !important;
  // border: 1px solid rgba(0, 0, 0, 0.125) !important;
}

button.list-group-item-dark:disabled,
button.list-group-item-dark.disabled,
a.list-group-item-dark:disabled,
a.list-group-item-dark.disabled {
  background-color: rgb(35, 35, 35);
  color: #aaa !important;
  // border: 1px solid rgba(0, 0, 0, 0.125) !important;
}

button.list-group-item-dark:focus,
button.list-group-item-dark.focus,
a.list-group-item-dark:focus,
a.list-group-item-dark.focus {
  background-color: $dark;
  // border: 1px solid rgba(0, 0, 0, 0.125);
  color: white;
}

.list-group-item.active {
  background-color: rgb(1, 164, 179);
  border-color: rgb(1, 164, 179);
}

.text-muted {
  color: #999 !important;
}

.mw-33 {
  min-width: 33%;
}
</style>
